import React, { memo, Fragment, useCallback, useEffect, useState } from 'react';
import { List, ButtonBase, ListItem, Grid, Typography, Divider, Box } from '@material-ui/core';
import { fade } from '@material-ui/core/styles';
import { DragIndicator } from '@material-ui/icons';
import { DraggableList, DraggableListItem } from '@campfire/draggable-list';
import { ProgramManagementProgram } from '../__generated__/ProgramManagementProgram';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useProgramManagementContext } from '../ProgramManagementContext';

interface Props {
  programs?: ProgramManagementProgram[];
  reorderMode?: boolean;
  onReorder?: (programIds: string[]) => void;
}
const ProgramsList = memo((props: Props) => {
  const { programs, reorderMode, onReorder } = props;
  const { theme } = useCampfireTheme();
  const { selectedProgramId, setSelectedProgramId } = useProgramManagementContext();
  const [orderedPrograms, setOrderedPrograms] = useState(programs ?? []);

  useEffect(() => {
    setOrderedPrograms(programs ?? []);
  }, [programs]);

  const handleReorder = useCallback(
    (startIdx: number, endIdx: number, reorderFn: (list: any[], s: number, e: number) => any[]) => {
      const reordered = reorderFn(orderedPrograms, startIdx, endIdx) as ProgramManagementProgram[];
      setOrderedPrograms(reordered);
      if (onReorder) onReorder(reordered.map((program) => program.programId));
    },
    [orderedPrograms, onReorder]
  );

  if (reorderMode) {
    return (
      <DraggableList onReorder={handleReorder}>
        {orderedPrograms.map((program) => (
          <DraggableListItem key={program.programId} id={program.programId} index={0}>
            <Box
              display='flex'
              alignItems='center'
              width='100%'
              style={{ padding: '12px 16px', borderBottom: `1px solid ${theme.color.grey.border}`, cursor: 'grab' }}
            >
              <DragIndicator style={{ fontSize: 18, color: theme.color.grey.neutral300, marginRight: 12 }} />
              <Typography variant='subtitle2' style={{ fontWeight: 'bold' }}>
                {program.name}
              </Typography>
            </Box>
          </DraggableListItem>
        ))}
      </DraggableList>
    );
  }

  return programs && programs.length >= 1 ? (
    <List style={{ padding: 0 }}>
      {programs.map((program, index) => (
        <Fragment key={program.programId}>
          <ButtonBase
            data-track='fs-prgMan-program-list-item'
            onClick={() => setSelectedProgramId(program.programId)}
            style={{ width: '100%' }}
          >
            <ListItem
              style={{ backgroundColor: selectedProgramId === program.programId ? theme.color.grey.border : undefined }}
            >
              <Grid container direction='column' style={{ paddingLeft: 24, paddingTop: 8, paddingBottom: 8 }}>
                <Grid item container alignItems='center' spacing={2}>
                  <Grid item style={{ padding: '4px 8px' }}>
                    <Typography variant='subtitle2' display='inline' style={{ fontWeight: 'bold' }}>
                      {program.name}
                    </Typography>
                  </Grid>
                  {program.isNew ? (
                    <Grid item style={{ padding: '4px 8px' }}>
                      <Box
                        style={{
                          backgroundColor: fade(theme.palette.primary.light, 0.4),
                          borderRadius: 2,
                          padding: '0px 4px',
                        }}
                      >
                        <Typography variant='caption' color='primary'>
                          {'New'}
                        </Typography>
                      </Box>
                    </Grid>
                  ) : null}
                </Grid>
              </Grid>
            </ListItem>
          </ButtonBase>
          {index < programs.length - 1 ? <Divider /> : null}
        </Fragment>
      ))}
    </List>
  ) : null;
});

export { ProgramsList };
